/**
 * Truegate + Apple Pay: request the payer's email in the native sheet and read
 * it back.
 *
 * Unlike Stripe, Truegate exposes no hook for either half of this. `initApplePay`
 * accepts only `{ id, buttonOptions }` — the Apple Pay request itself comes from
 * the payment-method config the SDK fetches from Truegate's backend and is
 * applied AFTER our payload is spread in, so it cannot be overridden — and the
 * only payment event the SDK emits, `PAYMENT_STATUS`, carries nothing but a
 * status. The SDK does read `payment.shippingContact.emailAddress` (native
 * gateways) / `event.payerEmail` (its Stripe gateway) and forward it to its own
 * backend, but never hands it to the page.
 *
 * So we intercept the two objects the SDK builds the sheet from, adding the
 * email to the request on the way in and capturing it on the way out:
 *
 *  - native gateways (Rapyd, Unlimit, Airwallex, Ecommpay, Shift4, Payabl,
 *    Colibrix) go through `new ApplePaySession(version, request)`;
 *  - the Stripe gateway goes through `stripe.paymentRequest(options)`.
 *
 * Both patches are installed only while a Truegate Apple Pay button is mounted
 * with `requestApplePayEmail` on, and every step is guarded: if anything about
 * the SDK changes, capture silently yields no email and the flow falls back to
 * the email-after-payment page, which is the behaviour with the flag off.
 *
 * Replace this with the SDK's own options the day Truegate supports them.
 */

type PayerEmailHandler = (email?: string) => void;

type Uninstall = () => void;

const isFunction = (value: any): value is Function => typeof value === 'function';

/**
 * Reads a property for a proxy `get` trap, binding methods to the real object so
 * they aren't called with the proxy as `this`. A locked-down own property (a
 * frozen object's, a class's `prototype`) has to come back untouched: returning
 * a bound copy would break the proxy invariant and throw.
 */
const getBound = (target: any, prop: string | symbol) => {
  const value = Reflect.get(target, prop, target);

  if (!isFunction(value)) {
    return value;
  }

  const descriptor = Object.getOwnPropertyDescriptor(target, prop);
  if (descriptor && !descriptor.configurable && !descriptor.writable) {
    return value;
  }

  return value.bind(target);
};

/** Apple only puts a field in the authorized payment if the sheet asked for it. */
const withEmailContactField = (request: any) => {
  const fields = Array.isArray(request?.requiredShippingContactFields)
    ? request.requiredShippingContactFields
    : [];

  if (fields.indexOf('email') !== -1) {
    return request;
  }

  return { ...request, requiredShippingContactFields: [...fields, 'email'] };
};

const wireApplePaySession = (
  session: any,
  request: any,
  onPayerEmail: PayerEmailHandler
) => {
  try {
    // Registered before the SDK assigns `onpaymentauthorized`, so it runs first
    // — the SDK completes the payment inside its own handler.
    session.addEventListener('paymentauthorized', (event: any) => {
      try {
        onPayerEmail(event?.payment?.shippingContact?.emailAddress);
      } catch (ex) {
        console.warn('[TRUEGATE APPLE PAY] payer email capture failed', ex);
      }
    });

    // Asking for a contact field makes the sheet emit `shippingcontactselected`
    // and wait for the page to answer before it lets the user pay. Truegate's
    // SDK never registers a handler — it never asks for contact fields — so
    // answer here, leaving the totals untouched.
    session.addEventListener('shippingcontactselected', () => {
      try {
        session.completeShippingContactSelection({
          newTotal: request?.total,
          newLineItems: request?.lineItems || [],
          errors: [],
        });
      } catch (ex) {
        console.warn('[TRUEGATE APPLE PAY] shipping contact selection failed', ex);
      }
    });
  } catch (ex) {
    console.warn('[TRUEGATE APPLE PAY] session wiring failed', ex);
  }

  return session;
};

/**
 * Replaces a global with a wrapped version, through an accessor rather than a
 * plain assignment: both globals we patch can be defined by a script that loads
 * AFTER the Apple Pay button mounts (Apple's `apple-pay-sdk.js`, and Stripe.js
 * which Truegate's own SDK loads inside `initApplePay`), so a value written
 * later has to be wrapped too. The wrapper is cached per underlying value.
 */
const patchGlobal = (name: string, wrapValue: (value: any) => any): Uninstall => {
  const win = window as any;

  let target = win[name];
  let wrappedFrom: any = null;
  let wrapped: any = null;

  const wrap = (value: any) => {
    if (!isFunction(value)) {
      return value;
    }
    if (wrappedFrom !== value) {
      wrappedFrom = value;
      wrapped = wrapValue(value);
    }
    return wrapped;
  };

  try {
    Object.defineProperty(win, name, {
      configurable: true,
      enumerable: true,
      get: () => wrap(target),
      set: (value) => {
        target = value;
      },
    });
  } catch (ex) {
    console.warn(`[TRUEGATE APPLE PAY] ${name} patch failed`, ex);
    return () => {};
  }

  return () => {
    try {
      Object.defineProperty(win, name, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: target,
      });
    } catch (ex) {
      console.warn(`[TRUEGATE APPLE PAY] ${name} restore failed`, ex);
    }
  };
};

/** Native gateways: `new ApplePaySession(version, request)`. */
const installApplePaySessionPatch = (onPayerEmail: PayerEmailHandler): Uninstall =>
  patchGlobal(
    'ApplePaySession',
    (ApplePaySession) =>
      new Proxy(ApplePaySession, {
        construct(ctor, args) {
          const [version, request] = args;
          const patched = withEmailContactField(request);
          return wireApplePaySession(
            Reflect.construct(ctor, [version, patched]),
            patched,
            onPayerEmail
          );
        },
        // Statics (`canMakePayments`, `STATUS_SUCCESS`, …) are read off the
        // global, so bind them to the real constructor, not to the proxy.
        get: (ctor, prop) => getBound(ctor, prop),
      })
  );

const wrapStripePaymentRequest = (
  paymentRequest: any,
  onPayerEmail: PayerEmailHandler
) =>
  new Proxy(paymentRequest, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target);

      if (prop === 'on' && isFunction(value)) {
        return (event: string, handler: Function) =>
          value.call(target, event, (payload: any) => {
            if (event === 'paymentmethod') {
              try {
                onPayerEmail(payload?.payerEmail);
              } catch (ex) {
                console.warn('[TRUEGATE APPLE PAY] payer email capture failed', ex);
              }
            }
            return handler(payload);
          });
      }

      return getBound(target, prop);
    },
  });

const wrapStripeInstance = (stripe: any, onPayerEmail: PayerEmailHandler) => {
  if (!stripe) {
    return stripe;
  }

  return new Proxy(stripe, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target);

      if (prop === 'paymentRequest' && isFunction(value)) {
        return (options: any) =>
          wrapStripePaymentRequest(
            value.call(target, { ...options, requestPayerEmail: true }),
            onPayerEmail
          );
      }

      return getBound(target, prop);
    },
  });
};

/** Truegate's Stripe gateway: `stripe.paymentRequest(options)`. */
const installStripePatch = (onPayerEmail: PayerEmailHandler): Uninstall =>
  patchGlobal(
    'Stripe',
    (Stripe) =>
      new Proxy(Stripe, {
        apply: (target, thisArg, args) =>
          wrapStripeInstance(Reflect.apply(target, thisArg, args), onPayerEmail),
        construct: (target, args) =>
          wrapStripeInstance(Reflect.construct(target, args), onPayerEmail),
      })
  );

/**
 * Makes the Truegate Apple Pay sheet ask for the payer's email and reports it
 * through `onPayerEmail` on every authorization — with the email when the sheet
 * returned one, with `undefined` when it didn't, so a stale value from an
 * earlier attempt is never reused.
 *
 * Returns an uninstall function; call it when the Apple Pay button unmounts.
 */
export const installTruegateApplePayEmailCapture = (
  onPayerEmail: PayerEmailHandler
): Uninstall => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const uninstalls = [
    installApplePaySessionPatch(onPayerEmail),
    installStripePatch(onPayerEmail),
  ];

  return () => {
    uninstalls.forEach((uninstall) => uninstall());
  };
};
